import { computed, makeObservable } from 'mobx'

import { durationInSeconds } from 'modules/core/lib/duration'
import { Model } from 'modules/core/lib/mobx-rest'

// Weekdays are defined as numbers in the backend
const SHIFT_DAYS = [0, 1, 2, 3, 4, 5, 6]

// Default rest days defined as Sat and Sun
export const DEFAULT_REST_DAYS = [5, 6]

enum DAY_NAMES_TO_SHIFT_DAYS {
  monday = 0,
  tuesday = 1,
  wednesday = 2,
  thursday = 3,
  friday = 4,
  saturday = 5,
  sunday = 6,
}

export const DAY_TYPE = Object.freeze({
  shift: 'shift',
  restDay: 'rest_day',
  emptyCell: 'empty_cell',
})

export const shiftDaysSortedByStartingWeekday = (
  startWeekday?: keyof typeof DAY_NAMES_TO_SHIFT_DAYS | null
) => {
  startWeekday = startWeekday || 'monday'

  const numberedFirstDayOfWeek = DAY_NAMES_TO_SHIFT_DAYS[startWeekday]
  const days = SHIFT_DAYS.slice(numberedFirstDayOfWeek).concat(
    SHIFT_DAYS.slice(0, numberedFirstDayOfWeek)
  )
  return days
}

interface IShiftConfiguration {
  start_at: string
  end_at: string
  id: string
  weekday: number
  day_type: string
}

// TODO: Deprecate when create/edit are migrated to GraphQL, we have only migrated the index (https://github.com/factorialco/factorial/pull/52951)
class ShiftConfiguration extends Model<IShiftConfiguration> {
  constructor(attr: IShiftConfiguration) {
    super(attr)
    makeObservable(this, {
      isRestDay: computed,
      isShift: computed,
      isEmptyCell: computed,
    })
  }

  static urlRoot() {
    return '/shift_management/shift_configurations'
  }

  get indexes() {
    return ['template_week_id']
  }

  duration(isTimeFormat24H: boolean): number {
    const startTime = this.attributes.start_at
    const endTime = this.attributes.end_at

    if (!startTime || !endTime) return 0

    return startTime === endTime
      ? 86400 // 24 hours in seconds
      : durationInSeconds(startTime, endTime, isTimeFormat24H)
  }

  get isRestDay(): boolean {
    return this.attributes.day_type === DAY_TYPE.restDay
  }

  get isShift(): boolean {
    return this.attributes.day_type === DAY_TYPE.shift
  }

  get isEmptyCell(): boolean {
    return this.attributes.day_type === DAY_TYPE.emptyCell
  }
}

export default ShiftConfiguration
